// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Column,
  Tile,
  Heading,
  Tag,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  ProgressBar,
  Loading,
  InlineNotification,
  Dropdown,
} from '@carbon/react';
import { ChartLine, Trophy, Time, Analytics } from '@carbon/icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

interface Variant {
  id: string;
  name: string;
  traffic_allocation: number;
  visitors: number;
  conversions: number;
  conversion_rate: number;
  confidence_interval: [number, number];
  is_control: boolean;
  is_winner?: boolean;
}

interface ABTest {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  created_at: string;
  started_at?: string;
  ended_at?: string;
  variants: Variant[];
  total_visitors: number;
  statistical_significance: number;
  confidence_level: number;
  minimum_sample_size: number;
  primary_metric: string;
  secondary_metrics: string[];
  description?: string;
  hypothesis?: string;
  winner_variant_id?: string;
}

interface TimeSeriesDataPoint {
  timestamp: string;
  date: string;
  visitors: number;
  conversions: number;
  conversion_rate: number;
  variant_id: string;
  variant_name: string;
}

interface ABTestAnalyticsProps {
  testId: string;
  onClose?: () => void;
}

export const ABTestAnalytics: React.FC<ABTestAnalyticsProps> = ({ testId, onClose }) => {
  const [test, setTest] = useState<ABTest | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);
  const [selectedMetric, setSelectedMetric] = useState('conversion_rate');
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [realTimeUpdate, setRealTimeUpdate] = useState(false);

  useEffect(() => {
    fetchTestData();
    fetchTimeSeriesData();

    // Set up real-time updates via WebSocket
    if (test?.status === 'running') {
      setupWebSocket();
      setRealTimeUpdate(true);
    }

    return () => {
      if (realTimeUpdate) {
        // Cleanup WebSocket connection
        setRealTimeUpdate(false);
      }
    };
  }, [testId, timeRange]);

  const fetchTestData = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/api/ab-tests/${testId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });

      if (response.ok) {
        const testData = await response.json();
        setTest(testData);
      } else {
        setError('Failed to load test data');
      }
    } catch (err) {
      setError('Network error loading test data');
    }
  };

  const fetchTimeSeriesData = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || '/api'}/api/ab-tests/${testId}/analytics?range=${timeRange}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTimeSeriesData(data.time_series || []);
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load analytics data');
      setLoading(false);
    }
  };

  const setupWebSocket = () => {
    // WebSocket connection for real-time updates
    const ws = new WebSocket(
      `${import.meta.env.VITE_WS_URL || window.location.origin.replace('http', 'ws')}/ab-testing/${testId}/analytics`
    );

    ws.onmessage = event => {
      const data = JSON.parse(event.data);
      if (data.type === 'test_update') {
        setTest(prev => (prev ? { ...prev, ...data.test } : null));
      }
      if (data.type === 'metrics_update') {
        setTimeSeriesData(prev => [...prev, data.dataPoint]);
      }
    };

    return () => ws.close();
  };

  const calculateStatisticalSignificance = (control: Variant, variant: Variant) => {
    if (!control || !variant || control.visitors < 100 || variant.visitors < 100) {
      return { significant: false, confidence: 0, improvement: 0 };
    }

    const controlRate = control.conversion_rate / 100;
    const variantRate = variant.conversion_rate / 100;

    // Z-test for proportions
    const pooledRate = (control.conversions + variant.conversions) / (control.visitors + variant.visitors);
    const standardError = Math.sqrt(pooledRate * (1 - pooledRate) * (1 / control.visitors + 1 / variant.visitors));

    if (standardError === 0) return { significant: false, confidence: 0, improvement: 0 };

    const zScore = Math.abs(variantRate - controlRate) / standardError;
    const confidence = (1 - 2 * (1 - normalCDF(Math.abs(zScore)))) * 100;
    const improvement = ((variantRate - controlRate) / controlRate) * 100;

    return {
      significant: confidence >= (test?.confidence_level || 95),
      confidence: confidence,
      improvement: improvement,
    };
  };

  // Normal cumulative distribution function approximation
  const normalCDF = (x: number) => {
    return 0.5 * (1 + erf(x / Math.sqrt(2)));
  };

  const erf = (x: number) => {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  const chartData = useMemo(() => {
    if (!timeSeriesData.length) return [];

    const groupedByDate = timeSeriesData.reduce((acc, point) => {
      const date = point.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      acc[date][`${point.variant_name}_${selectedMetric}`] = point[selectedMetric as keyof TimeSeriesDataPoint];
      return acc;
    }, {} as any);

    return Object.values(groupedByDate);
  }, [timeSeriesData, selectedMetric]);

  const metricOptions = [
    { id: 'conversion_rate', text: 'Conversion Rate (%)' },
    { id: 'visitors', text: 'Visitors' },
    { id: 'conversions', text: 'Conversions' },
  ];

  const timeRangeOptions = [
    { id: '24h', text: 'Last 24 Hours' },
    { id: '7d', text: 'Last 7 Days' },
    { id: '30d', text: 'Last 30 Days' },
    { id: 'all', text: 'All Time' },
  ];

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Loading description="Loading analytics data..." />
      </div>
    );
  }

  if (error || !test) {
    return (
      <InlineNotification
        kind="error"
        title="Error loading analytics"
        subtitle={error || 'Test not found'}
        onCloseButtonClick={onClose}
      />
    );
  }

  const controlVariant = test.variants.find(v => v.is_control);
  const testVariants = test.variants.filter(v => !v.is_control);

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Heading size="lg" style={{ marginBottom: '0.5rem' }}>
            {test.name} - Analytics
          </Heading>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <Tag type={test.status === 'running' ? 'green' : test.status === 'completed' ? 'blue' : 'gray'}>
              {test.status.charAt(0).toUpperCase() + test.status.slice(1)}
            </Tag>
            {realTimeUpdate && (
              <Tag type="green">
                <Time size={16} style={{ marginRight: '0.25rem' }} />
                Real-time
              </Tag>
            )}
            <span style={{ fontSize: '0.875rem', color: '#6f6f6f' }}>
              Total Visitors: {test.total_visitors.toLocaleString()}
            </span>
          </div>
        </div>
        {onClose && (
          <Button kind="secondary" onClick={onClose}>
            Close Analytics
          </Button>
        )}
      </div>

      <Grid>
        {/* Key Metrics */}
        <Column lg={16} md={8} sm={4}>
          <Tile style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}
            >
              <Heading size="md">Performance Overview</Heading>
              {test.statistical_significance >= (test.confidence_level || 95) && (
                <Tag type="green">
                  <Trophy size={16} style={{ marginRight: '0.25rem' }} />
                  Statistically Significant
                </Tag>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {test.variants.map(variant => {
                const stats =
                  controlVariant && !variant.is_control
                    ? calculateStatisticalSignificance(controlVariant, variant)
                    : null;

                return (
                  <div
                    key={variant.id}
                    style={{
                      padding: '1rem',
                      border: variant.is_winner ? '2px solid #42be65' : '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: variant.is_winner ? '#f6fffa' : '#ffffff',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <strong>{variant.name}</strong>
                      {variant.is_control && <Tag type="gray">Control</Tag>}
                      {variant.is_winner && <Tag type="green">Winner</Tag>}
                    </div>

                    <div style={{ marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6f6f6f' }}>Conversion Rate</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{variant.conversion_rate.toFixed(2)}%</div>
                    </div>

                    <div
                      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem' }}
                    >
                      <div>
                        <div style={{ color: '#6f6f6f' }}>Visitors</div>
                        <div>{variant.visitors.toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ color: '#6f6f6f' }}>Conversions</div>
                        <div>{variant.conversions.toLocaleString()}</div>
                      </div>
                    </div>

                    {stats && (
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '0.875rem', color: '#6f6f6f' }}>vs Control</div>
                        <div
                          style={{
                            color: stats.improvement > 0 ? '#42be65' : stats.improvement < 0 ? '#da1e28' : '#6f6f6f',
                            fontWeight: '600',
                          }}
                        >
                          {stats.improvement > 0 ? '+' : ''}
                          {stats.improvement.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6f6f6f' }}>
                          {stats.confidence.toFixed(1)}% confidence
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: '0.5rem' }}>
                      <ProgressBar
                        value={Math.min(variant.visitors, test.minimum_sample_size)}
                        max={test.minimum_sample_size}
                        label="Sample Size Progress"
                        size="small"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Tile>
        </Column>

        {/* Time Series Chart */}
        <Column lg={16} md={8} sm={4}>
          <Tile style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}
            >
              <Heading size="md">Performance Over Time</Heading>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Dropdown
                  id="metric-selector"
                  items={metricOptions}
                  selectedItem={metricOptions.find(opt => opt.id === selectedMetric)}
                  onChange={({ selectedItem }) => setSelectedMetric(selectedItem?.id || 'conversion_rate')}
                  titleText="Metric"
                  label="Select metric"
                  size="md"
                />
                <Dropdown
                  id="time-range-selector"
                  items={timeRangeOptions}
                  selectedItem={timeRangeOptions.find(opt => opt.id === timeRange)}
                  onChange={({ selectedItem }) => setTimeRange(selectedItem?.id || '7d')}
                  titleText="Time Range"
                  label="Select time range"
                  size="md"
                />
              </div>
            </div>

            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {test.variants.map((variant, index) => (
                    <Line
                      key={variant.id}
                      type="monotone"
                      dataKey={`${variant.name}_${selectedMetric}`}
                      stroke={variant.is_control ? '#0f62fe' : `hsl(${(index * 60) % 360}, 70%, 50%)`}
                      strokeWidth={variant.is_control ? 3 : 2}
                      strokeDasharray={variant.is_control ? '0' : '5,5'}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Tile>
        </Column>

        {/* Variant Comparison Table */}
        <Column lg={16} md={8} sm={4}>
          <Tile style={{ padding: '1.5rem' }}>
            <Heading size="md" style={{ marginBottom: '1rem' }}>
              Detailed Comparison
            </Heading>

            <DataTable
              rows={test.variants.map(variant => ({
                id: variant.id,
                name: variant.name,
                type: variant.is_control ? 'Control' : 'Variant',
                visitors: variant.visitors.toLocaleString(),
                conversions: variant.conversions.toLocaleString(),
                conversion_rate: `${variant.conversion_rate.toFixed(2)}%`,
                confidence_interval: `${variant.confidence_interval[0].toFixed(2)}% - ${variant.confidence_interval[1].toFixed(2)}%`,
                traffic_allocation: `${variant.traffic_allocation}%`,
                winner: variant.is_winner ? 'Yes' : 'No',
              }))}
              headers={[
                { key: 'name', header: 'Variant Name' },
                { key: 'type', header: 'Type' },
                { key: 'visitors', header: 'Visitors' },
                { key: 'conversions', header: 'Conversions' },
                { key: 'conversion_rate', header: 'Conversion Rate' },
                { key: 'confidence_interval', header: '95% CI' },
                { key: 'traffic_allocation', header: 'Traffic %' },
                { key: 'winner', header: 'Winner' },
              ]}
            >
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow {...getRowProps({ row })}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default ABTestAnalytics;
